import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AirlineType,
  PrinterType,
  WeatherOverrideAirline,
} from '../enums/enum';
import { datesCreator } from '../utils/dateHandler';
import AppStateType from '../@types/AppState.type';
import { AxiosError } from 'axios';
import { PacketType } from '../@types/Packet.type';

const initialAppState: AppStateType = {
  airlineTypeData: AirlineType.AS,
  printerTypeData: PrinterType.LASER,
  packetTypeData: 'BP',
  errorState: undefined,
  selectStationData: undefined,
  selectPrinterData: undefined,
  networkPath: undefined,
  flightNumber: undefined,
  flightOrigin: undefined,
  flightDate: datesCreator()[1].toString(),
  weatherOverideAirlineData: WeatherOverrideAirline.NONE,
  reqValidity: {
    airlineType: true,
    printerType: true,
    selectStation: true,
    selectPrinter: true,
    packetType: true,
    flightOrigin: true,
    flightNumber: true,
    flightDate: true,
  },
  packetReqLoading: false,
};

const appStateSlice = createSlice({
  name: 'appState',
  initialState: initialAppState,
  reducers: {
    // Updates the error state
    updateErrorState(
      state,
      action: PayloadAction<AxiosError | Error | undefined>,
    ) {
      state.errorState = action.payload;
    },

    // Updates the airline type data (AS/QX). Gets called with the radio button used to select the airline type
    updateAirlineTypeData(state, action: PayloadAction<string>) {
      state.airlineTypeData = action.payload;
    },

    // Updates the printer type data (Laser/FILE). Gets called with the radio button used to select the
    // printer type
    updatePrinterTypeData(state, action: PayloadAction<PrinterType>) {
      state.printerTypeData = action.payload;
    },

    // Updates the packet type data (Briefing Packet/ Take Off Perf etc.). Gets called with the radio button
    // used to select the packet type
    updatePacketTypeData(state, action: PayloadAction<PacketType>) {
      state.packetTypeData = action.payload;
    },

    // Updates the weather override airline data (AS/QX/NONE). Gets called after calling the GetWeatherOverride
    // API
    updateWeatherOverideAirlineData(state, action: PayloadAction<string>) {
      state.weatherOverideAirlineData = action.payload;
    },

    // Updates the validation status of the different fileds in the application
    updateValidityStatus(
      state,
      action: PayloadAction<typeof initialAppState.reqValidity>,
    ) {
      state.reqValidity = action.payload;
    },

    // Handles the updation of the printer network path
    updateNetworkPath(state, action: PayloadAction<string | undefined>) {
      state.networkPath = action.payload;
    },

    // Handles the updation of the station dropdown
    handleStationChange(state, action: PayloadAction<string | undefined>) {
      state.selectStationData = action.payload;
      state.selectPrinterData = state.selectPrinterData?.trim()
        ? ''
        : state.selectPrinterData;
    },

    // Handles the updatation of the printer dropdown
    handlePrinterChange(state, action: PayloadAction<string | undefined>) {
      state.selectPrinterData = action.payload;
    },

    // Handles the updation of the flight number input field
    handleFlightNumber(state, action: PayloadAction<string | undefined>) {
      state.flightNumber = action.payload;
    },

    // Handles the updation of the flight origin input field
    handleFlightOrigin(state, action: PayloadAction<string | undefined>) {
      state.flightOrigin = action.payload;
    },

    // Handles the updation of the flight date input field
    handleFlightDate(state, action: PayloadAction<string>) {
      state.flightDate = action.payload;
    },

    // Handles the loader state after user clicks the Request Packet button
    handlePacketReqLoading(state, action: PayloadAction<boolean>) {
      state.packetReqLoading = action.payload;
    },

    // Resets all the validation errors to all true.
    resetAllValidationErrors(state) {
      state.reqValidity = {
        airlineType: true,
        printerType: true,
        selectStation: true,
        selectPrinter: true,
        packetType: true,
        flightOrigin: true,
        flightNumber: true,
        flightDate: true,
      };
    },

    // Resets the station data to undefined
    resetStationData(state) {
      state.selectStationData = undefined;
    },

    // Resets the printer data to undefined
    resetPrinterData(state) {
      state.selectPrinterData = undefined;
    },

    // Resets the station and printer validation error to true
    resetStationAndPrinterValidationError(state) {
      state.reqValidity = {
        ...state.reqValidity,
        selectStation: true,
        selectPrinter: true,
      };
    },
  },
});

export default appStateSlice;
